import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ChevronLeft, Loader2, Play, Upload, CheckCircle2, Cpu, Save, RotateCcw,
  Move, Grip, DoorClosed, PencilLine, Image as ImageIcon, Camera,
  PenLine, DoorOpen, AppWindow, Trash2, Plus, Undo2, Redo2, Ruler, Magnet, Spline, Crosshair,
  Table2, FileDown, FileSpreadsheet, Map, Triangle,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, API_BASE } from "@/api/client";
import type { Room, Geometry, Scan, GeometryStatus } from "@/api/types";
import { WALL_KINDS, wallKindOf, wallThicknessOf, WALL_THICKNESS_MIN, WALL_THICKNESS_MAX, metreForGeom, floorRegions, resolveZoneName, pointInPoly, polyArea, type WallKind } from "@/lib/geo";
import { GeometryBadge, StatusBadge, DriftBadge } from "@/components/StatusBadge";
import { GeometryCanvas2D, EditableGeom, EditorTool } from "@/components/GeometryCanvas2D";
import { GeometryViewer3D } from "@/components/GeometryViewer3D";
import { ProcessingOverlay } from "@/components/ProcessingOverlay";
import { useGeometryHistory } from "@/hooks/useGeometryHistory";

const GRID = 0.1;

const DEVICE = { brand: "Google", model: "Pixel 8", android_version: "14", arcore_available: true, depth_available: false };

function Bar({ label, value }: { label: string; value: number }) {
  const pct = Math.round((value || 0) * 100);
  return (
    <div>
      <div className="flex justify-between text-xs"><span className="text-muted-foreground">{label}</span><span className="font-mono">{pct}%</span></div>
      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-secondary">
        <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function RoomView() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState<"details" | "2d" | "3d">(() => {
    const t = new URLSearchParams(window.location.search).get("tab") || "";
    return (["details", "2d", "3d"].includes(t) ? t : "details") as any;
  });
  const [currentScan, setCurrentScan] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // editor state
  const hist = useGeometryHistory(null);
  const editGeom = hist.present;
  const [tool, setTool] = useState<EditorTool>("move-vertex");
  const [selOpening, setSelOpening] = useState<string | null>(null);
  const [drawFrom, setDrawFrom] = useState<string | null>(null);
  const [drawStart, setDrawStart] = useState<string | null>(null);
  const [selWall, setSelWall] = useState<string | null>(null);
  const [zoneEdit, setZoneEdit] = useState<{ cx: number; cy: number; name: string } | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [planLoading, setPlanLoading] = useState(false);
  const [snapOn, setSnapOn] = useState(true);
  const [angleSnap, setAngleSnap] = useState(true);
  const [showDims, setShowDims] = useState(true);
  const [recenter, setRecenter] = useState(0);
  const [renameVal, setRenameVal] = useState("");
  const [lowCeilDraft, setLowCeilDraft] = useState<{ x: number; y: number }[]>([]);

  // 3d state
  const [rotation, setRotation] = useState(35);
  const [styleStatus, setStyleStatus] = useState<GeometryStatus | undefined>(undefined);
  const [showWalls, setShowWalls] = useState(true);
  const [showCeiling, setShowCeiling] = useState(false);
  const [wireframe, setWireframe] = useState(false);

  const { data: room } = useQuery<Room>({ queryKey: ["room", id], queryFn: () => api.getRoom(id) });
  const { data: geom } = useQuery<Geometry | null>({
    queryKey: ["geometry", id],
    queryFn: async () => { try { return await api.latestGeometry(id); } catch { return null; } },
  });
  const { data: scans = [] } = useQuery<Scan[]>({ queryKey: ["scans", id], queryFn: () => api.listScans(id) });

  useEffect(() => { if (room) setRenameVal(room.name); }, [room?.name]);
  useEffect(() => {
    if (geom) hist.reset({ vertices: geom.vertices, walls: geom.walls as any, openings: geom.openings, zones: geom.zones || [], lowCeilingArea: geom.low_ceiling_area || 0, lowCeilingPolys: geom.low_ceiling_polys || [], height: geom.height, status: geom.status });
    else hist.reset(null);
  }, [geom?._id]);

  // keyboard: undo / redo (active in 2D editor)
  useEffect(() => {
    if (tab !== "2d") return;
    const onKey = (e: KeyboardEvent) => {
      const mod = e.ctrlKey || e.metaKey;
      if (!mod || e.key.toLowerCase() !== "z") return;
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      e.preventDefault();
      if (e.shiftKey) hist.redo(); else hist.undo();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [tab, hist.undo, hist.redo]);

  const startMut = useMutation({
    mutationFn: () => api.startScan(id, DEVICE),
    onSuccess: (s) => { setCurrentScan(s._id); toast.success("Session de scan démarrée"); qc.invalidateQueries({ queryKey: ["scans", id] }); qc.invalidateQueries({ queryKey: ["room", id] }); },
    onError: (e: any) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const uploadMut = useMutation({
    mutationFn: (files: File[]) => api.uploadFrames(id, currentScan!, files),
    onSuccess: (s) => { toast.success(`${s.frames.length} frame(s) téléversée(s)`); qc.invalidateQueries({ queryKey: ["scans", id] }); },
    onError: (e: any) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const completeMut = useMutation({
    mutationFn: () => api.completeScan(id, currentScan!),
    onSuccess: () => { toast.success("Scan finalisé — géométrie estimée créée"); setCurrentScan(null); qc.invalidateQueries({ queryKey: ["scans", id] }); qc.invalidateQueries({ queryKey: ["room", id] }); qc.invalidateQueries({ queryKey: ["geometry", id] }); },
    onError: (e: any) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const reconMut = useMutation({
    mutationFn: () => api.reconstruct(id),
    onSuccess: () => { toast.success("Reconstruction terminée"); qc.invalidateQueries({ queryKey: ["room", id] }); qc.invalidateQueries({ queryKey: ["geometry", id] }); },
    onError: (e: any) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const saveMut = useMutation({
    mutationFn: () => api.manualEdit(id, { vertices: editGeom!.vertices, walls: editGeom!.walls, openings: editGeom!.openings, zones: editGeom!.zones || [], low_ceiling_area: editGeom!.lowCeilingArea || 0, low_ceiling_polys: editGeom!.lowCeilingPolys || [], height: editGeom!.height, note: "web edit" }),
    onSuccess: () => { toast.success("Nouvelle version de géométrie enregistrée"); qc.invalidateQueries({ queryKey: ["geometry", id] }); qc.invalidateQueries({ queryKey: ["room", id] }); },
    onError: (e: any) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const renameMut = useMutation({
    mutationFn: () => api.updateRoom(id, { name: renameVal } as any),
    onSuccess: () => { toast.success("Pièce renommée"); qc.invalidateQueries({ queryKey: ["room", id] }); },
    onError: (e: any) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  if (!room) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const allFrames = scans.flatMap((s) => s.frames || []);
  const selectedOpening = editGeom?.openings.find((o) => o.id === selOpening) || null;

  const updateOpening = (patch: Partial<{ position: number; width: number }>) => {
    if (!editGeom || !selOpening) return;
    hist.live({ ...editGeom, openings: editGeom.openings.map((o) => (o.id === selOpening ? { ...o, ...patch } : o)) });
  };

  const selectedWall = editGeom?.walls.find((w) => w.id === selWall) || null;

  const handleWallDblClick = (wallId: string) => setSelWall(wallId);

  const setWallKind = (kind: WallKind) => {
    if (!editGeom || !selWall) return;
    hist.commit({ ...editGeom, walls: editGeom.walls.map((w) => (w.id === selWall ? { ...w, kind, thickness: WALL_KINDS[kind].def } : w)) });
  };
  const setWallThickness = (thickness: number) => {
    if (!editGeom || !selWall) return;
    hist.live({ ...editGeom, walls: editGeom.walls.map((w) => (w.id === selWall ? { ...w, thickness } : w)) });
  };

  // zone renaming (persisted in geometry.zones, matched by anchor point-in-polygon)
  const setLowCeilingLive = (v: number) => { if (editGeom) hist.live({ ...editGeom, lowCeilingArea: Math.max(0, v) }); };
  const handleZoneDblClick = (cx: number, cy: number) => {
    if (!editGeom) return;
    const { regions } = floorRegions(editGeom.vertices, editGeom.walls);
    const idx = regions.findIndex((r) => Math.abs(r.cx - cx) < 1e-6 && Math.abs(r.cy - cy) < 1e-6);
    const reg = idx >= 0 ? regions[idx] : null;
    if (!reg) return;
    setZoneEdit({ cx: reg.cx, cy: reg.cy, name: resolveZoneName(reg, editGeom.zones, idx >= 0 ? idx : 0) });
  };
  const saveZoneName = () => {
    if (!editGeom || !zoneEdit) return;
    const name = zoneEdit.name.trim();
    const zones = [...(editGeom.zones || [])];
    // replace any existing anchor that falls in this region, else add
    const { regions } = floorRegions(editGeom.vertices, editGeom.walls);
    const reg = regions.find((r) => Math.abs(r.cx - zoneEdit.cx) < 1e-6 && Math.abs(r.cy - zoneEdit.cy) < 1e-6);
    const kept = reg ? zones.filter((z) => !pointInPoly({ x: z.x, y: z.y }, reg.poly)) : zones;
    if (name) kept.push({ name, x: zoneEdit.cx, y: zoneEdit.cy });
    hist.commit({ ...editGeom, zones: kept });
    setZoneEdit(null);
    if (name) toast.success(`Zone nommée « ${name} »`);
  };

  const rid = (p: string) => p + "_" + Math.random().toString(36).slice(2, 8);

  // metre (quantity take-off) + exports
  const metre = editGeom ? metreForGeom(editGeom) : null;
  const dl = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };
  const exportMetreCsv = () => {
    if (!metre) return;
    const rows: string[] = ["Rubrique;Détail;Au sol (m²);Habitable (m²);Unité"];
    metre.zones.forEach((z, i) => rows.push(`Zone;${z.name || `Zone ${i + 1}`};${z.area.toFixed(2)};${z.habitable.toFixed(2)};m²`));
    rows.push(`Total;Surface;${metre.total.toFixed(2)};${metre.totalHabitable.toFixed(2)};m²`);
    rows.push(`Loi Carrez;Surface ≥ 1,80 m (sous-pente ${metre.lowCeiling.toFixed(2)} déduite);${metre.carrez.toFixed(2)};;m²`);
    rows.push(`Contour;Périmètre;${metre.perimeter.toFixed(2)};;m`);
    rows.push(`Murs;Mur extérieur;${metre.byKind.exterior.toFixed(2)};;m`);
    rows.push(`Murs;Mur porteur;${metre.byKind.bearing.toFixed(2)};;m`);
    rows.push(`Murs;Cloison;${metre.byKind.partition.toFixed(2)};;m`);
    dl(new Blob(["\ufeff" + rows.join("\n")], { type: "text/csv;charset=utf-8" }), `metre_${room?.name || "piece"}.csv`);
  };
  const exportMetrePdf = async () => {
    setPdfLoading(true);
    try {
      const blob = await api.metrePdf(id);
      dl(blob, `metre_${room?.name || "piece"}.pdf`);
    } catch (e) {
      toast.error("Échec de l'export PDF. Enregistrez d'abord la géométrie.");
    } finally {
      setPdfLoading(false);
    }
  };
  const exportPlanPdf = async () => {
    setPlanLoading(true);
    try {
      const blob = await api.planPdf(id);
      dl(blob, `plan_${room?.name || "piece"}.pdf`);
    } catch (e) {
      toast.error("Échec de l'export du plan. Enregistrez d'abord la géométrie.");
    } finally {
      setPlanLoading(false);
    }
  };

  const selectTool = (t: EditorTool) => { setTool(t); if (t !== "draw-wall") { setDrawFrom(null); setDrawStart(null); } if (t !== "draw-lowceiling") setLowCeilDraft([]); };

  const handleEmptyClick = (pt: { x: number; y: number }) => {
    if (!editGeom || tool !== "draw-wall") return;
    // snap to an existing vertex when close, so partitions can attach to the room (creates sub-spaces)
    const near = editGeom.vertices.find((v) => v.id !== drawFrom && Math.hypot(v.x - pt.x, v.y - pt.y) <= GRID * 2);
    const targetId = near ? near.id : rid("v");
    const vertices = near ? editGeom.vertices : [...editGeom.vertices, { id: targetId, x: pt.x, y: pt.y }];
    const walls = drawFrom && drawFrom !== targetId
      ? [...editGeom.walls, { id: rid("w"), from: drawFrom, to: targetId }]
      : editGeom.walls;
    hist.commit({ ...editGeom, vertices, walls });
    if (!drawFrom) setDrawStart(targetId);
    setDrawFrom(targetId);
  };

  const handleClosePath = () => {
    if (!editGeom || !drawStart || !drawFrom || drawFrom === drawStart) return;
    hist.commit({ ...editGeom, walls: [...editGeom.walls, { id: rid("w"), from: drawFrom, to: drawStart }] });
    setDrawFrom(null);
    setDrawStart(null);
    toast.success("Tracé fermé — pièce créée");
  };

  const handleLowCeilPoint = (pt: { x: number; y: number }) => setLowCeilDraft((d) => [...d, pt]);
  const handleLowCeilClose = () => {
    if (!editGeom || lowCeilDraft.length < 3) { setLowCeilDraft([]); return; }
    hist.commit({ ...editGeom, lowCeilingPolys: [...(editGeom.lowCeilingPolys || []), lowCeilDraft] });
    setLowCeilDraft([]);
    toast.success("Zone sous-pente ajoutée");
  };
  const removeLowCeilPoly = (i: number) => {
    if (!editGeom) return;
    hist.commit({ ...editGeom, lowCeilingPolys: (editGeom.lowCeilingPolys || []).filter((_, j) => j !== i) });
  };

  const handleWallClick = (wallId: string, position: number) => {
    if (!editGeom) return;
    if (tool === "add-door" || tool === "add-window") {
      const isDoor = tool === "add-door";
      const op = {
        id: rid("o"), wall_id: wallId, type: isDoor ? "door" : "window",
        position: Math.round(position * 100) / 100,
        width: isDoor ? 0.9 : 1.0,
        height: isDoor ? 2.05 : 1.1,
        sill: isDoor ? 0 : 0.9,
      };
      hist.commit({ ...editGeom, openings: [...editGeom.openings, op] });
      setSelOpening(op.id);
      toast.success(isDoor ? "Porte ajoutée" : "Fenêtre ajoutée");
    } else if (tool === "delete") {
      hist.commit({
        ...editGeom,
        walls: editGeom.walls.filter((w) => w.id !== wallId),
        openings: editGeom.openings.filter((o) => o.wall_id !== wallId),
      });
      if (selWall === wallId) setSelWall(null);
    }
  };

  const handleVertexClick = (vid: string) => {
    if (!editGeom || tool !== "delete") return;
    const removedWalls = editGeom.walls.filter((w) => w.from === vid || w.to === vid).map((w) => w.id);
    hist.commit({
      ...editGeom,
      vertices: editGeom.vertices.filter((v) => v.id !== vid),
      walls: editGeom.walls.filter((w) => w.from !== vid && w.to !== vid),
      openings: editGeom.openings.filter((o) => !removedWalls.includes(o.wall_id)),
    });
    if (drawFrom === vid) setDrawFrom(null);
    if (drawStart === vid) setDrawStart(null);
    if (selWall && removedWalls.includes(selWall)) setSelWall(null);
  };

  const handleOpeningClick = (oid: string) => {
    if (!editGeom) return;
    if (tool === "delete") {
      hist.commit({ ...editGeom, openings: editGeom.openings.filter((o) => o.id !== oid) });
      if (selOpening === oid) setSelOpening(null);
    }
  };

  return (
    <div className="space-y-6">
      <button onClick={() => navigate(`/dwellings/${room.dwelling_id}`)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ChevronLeft className="h-4 w-4" /> Logement
      </button>

      <div className="flex flex-col gap-4 rounded-xl border border-border/60 bg-card p-6 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold tracking-tight">{room.name}</h1>
            <GeometryBadge status={room.geometry_status} testId="room-geometry-status-badge" />
            <StatusBadge status={room.scan_status} />
            {room.drift && <DriftBadge drift={room.drift} testId="room-drift-badge" />}
          </div>
          <p className="mt-1 uppercase-label text-muted-foreground">{room.room_type} · niveau {room.floor_index}</p>
        </div>
        <div className="text-right">
          <div className="uppercase-label text-muted-foreground">Confiance globale</div>
          <div data-testid="room-confidence-score" className="font-mono text-4xl font-bold text-primary">{Math.round((room.confidence?.overall || 0) * 100)}%</div>
        </div>
      </div>

      <div className="flex gap-1 rounded-lg border border-border p-1">
        {([["details", "Détails"], ["2d", "Éditeur 2D"], ["3d", "Vue 3D"]] as const).map(([v, label]) => (
          <button
            key={v}
            data-testid={v === "2d" ? "open-2d-editor-button" : v === "3d" ? "open-3d-viewer-button" : "open-details-button"}
            onClick={() => setTab(v)}
            className={`flex-1 rounded-md py-2 text-sm font-medium transition-colors ${tab === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "details" && (
        <div className="grid gap-6 lg:grid-cols-3">
          {/* scan pipeline */}
          <div className="space-y-4 lg:col-span-2">
            <div className="rounded-xl border border-border/60 bg-card p-5">
              <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold"><Camera className="h-5 w-5 text-primary" /> Pipeline de scan</h3>
              <div className="flex flex-wrap gap-2">
                <button data-testid="start-scan-button" onClick={() => startMut.mutate()} disabled={startMut.isPending} className="flex items-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50">
                  {startMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Démarrer un scan
                </button>
                <button data-testid="upload-frames-button" onClick={() => fileRef.current?.click()} disabled={!currentScan || uploadMut.isPending} className="flex items-center gap-2 rounded-md border border-border px-4 py-2.5 text-sm hover:border-primary/40 disabled:opacity-40">
                  {uploadMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Téléverser frames
                </button>
                <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { if (e.target.files) uploadMut.mutate(Array.from(e.target.files)); e.target.value = ""; }} />
                <button data-testid="complete-scan-button" onClick={() => completeMut.mutate()} disabled={!currentScan || completeMut.isPending} className="flex items-center gap-2 rounded-md border border-border px-4 py-2.5 text-sm hover:border-emerald-500/40 disabled:opacity-40">
                  {completeMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4 text-emerald-400" />} Finaliser
                </button>
                <button data-testid="reconstruct-button" onClick={() => reconMut.mutate()} disabled={reconMut.isPending} className="flex items-center gap-2 rounded-md border border-cyan-500/30 bg-cyan-500/10 px-4 py-2.5 text-sm text-cyan-400 hover:bg-cyan-500/20 disabled:opacity-40">
                  {reconMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Cpu className="h-4 w-4" />} Reconstruire
                </button>
              </div>
              {currentScan && <p className="mt-3 uppercase-label text-amber-400">Session active — téléversez des frames puis finalisez.</p>}
            </div>

            <div className="rounded-xl border border-border/60 bg-card p-5">
              <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold"><ImageIcon className="h-5 w-5 text-primary" /> Keyframes RGB ({allFrames.length})</h3>
              {allFrames.length === 0 ? (
                <p className="text-sm text-muted-foreground">Aucune frame téléversée.</p>
              ) : (
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                  {allFrames.slice(0, 12).map((f) => (
                    <img key={f.id} data-testid={`frame-${f.id}`} src={`${API_BASE}/files/${f.storage_path}`} alt={f.original_filename} className="aspect-square w-full rounded-md border border-border object-cover" />
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-xl border border-border/60 bg-card p-5">
              <h3 className="mb-3 text-lg font-semibold">Sessions de scan ({scans.length})</h3>
              <div className="space-y-2">
                {scans.map((s) => (
                  <div key={s._id} className="flex items-center justify-between rounded-lg border border-border/50 bg-background/40 p-3 text-sm">
                    <span className="font-mono text-xs text-muted-foreground">{s.device_info?.brand} {s.device_info?.model} · {s.capture_mode}</span>
                    <StatusBadge status={s.status === "uploaded" ? "uploaded" : s.status === "capturing" ? "capturing" : "processed"} />
                  </div>
                ))}
                {scans.length === 0 && <p className="text-sm text-muted-foreground">Aucune session.</p>}
              </div>
            </div>
          </div>

          {/* side */}
          <div className="space-y-4">
            <div className="rounded-xl border border-border/60 bg-card p-5">
              <h3 className="mb-4 text-lg font-semibold">Confiance</h3>
              <div className="space-y-3">
                <Bar label="Murs" value={room.confidence?.walls || 0} />
                <Bar label="Ouvertures" value={room.confidence?.openings || 0} />
                <Bar label="Sommets" value={room.confidence?.vertices || 0} />
              </div>
              {room.drift && (
                <div className="mt-4 flex items-center justify-between border-t border-border/50 pt-3">
                  <span className="uppercase-label text-muted-foreground">Dérive IMU estimée</span>
                  <DriftBadge drift={room.drift} />
                </div>
              )}
            </div>
            <div className="rounded-xl border border-border/60 bg-card p-5">
              <h3 className="mb-3 text-lg font-semibold">Renommer</h3>
              <div className="flex gap-2">
                <input data-testid="room-rename-input" value={renameVal} onChange={(e) => setRenameVal(e.target.value)} className="w-full rounded-md border border-input bg-background/60 px-3 py-2 text-sm outline-none focus:border-primary" />
                <button data-testid="room-rename-save" onClick={() => renameMut.mutate()} className="rounded-md bg-primary px-3 text-sm font-semibold text-primary-foreground hover:opacity-90">OK</button>
              </div>
            </div>
            {geom && (
              <div className="rounded-xl border border-border/60 bg-card p-5">
                <h3 className="mb-2 text-lg font-semibold">Géométrie</h3>
                <p className="uppercase-label text-muted-foreground">Version {geom.version} · {geom.source}</p>
                <div className="mt-3"><GeometryCanvas2D geom={{ vertices: geom.vertices, walls: geom.walls as any, openings: geom.openings, height: geom.height, status: geom.status }} /></div>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "2d" && (
        editGeom ? (
          <div className="grid gap-4 lg:grid-cols-4">
            <div className="space-y-3 lg:col-span-1">
            <div className="rounded-xl border border-border/60 bg-card p-4">
              <div className="uppercase-label mb-2 text-muted-foreground">Historique</div>
              <div className="flex gap-2">
                <button data-testid="undo-button" onClick={() => hist.undo()} disabled={!hist.canUndo} title="Annuler (Ctrl+Z)" className="flex flex-1 items-center justify-center gap-1.5 rounded-md border border-border px-3 py-2 text-sm hover:border-primary/40 disabled:opacity-35 disabled:cursor-not-allowed">
                  <Undo2 className="h-4 w-4" /> Annuler
                </button>
                <button data-testid="redo-button" onClick={() => hist.redo()} disabled={!hist.canRedo} title="Rétablir (Ctrl+Maj+Z)" className="flex flex-1 items-center justify-center gap-1.5 rounded-md border border-border px-3 py-2 text-sm hover:border-primary/40 disabled:opacity-35 disabled:cursor-not-allowed">
                  <Redo2 className="h-4 w-4" /> Rétablir
                </button>
              </div>
              <div className="mt-3 space-y-1.5">
                <button data-testid="toggle-snap" onClick={() => setSnapOn((v) => !v)} className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-sm ${snapOn ? "bg-primary/15 border border-primary/40 text-foreground" : "bg-background/40 text-muted-foreground"}`}>
                  <span className="flex items-center gap-2"><Magnet className="h-4 w-4" /> Aimanter (grille {GRID} m)</span>
                  <span className="font-mono text-xs">{snapOn ? "ON" : "OFF"}</span>
                </button>
                <button data-testid="toggle-angle" onClick={() => setAngleSnap((v) => !v)} className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-sm ${angleSnap ? "bg-primary/15 border border-primary/40 text-foreground" : "bg-background/40 text-muted-foreground"}`}>
                  <span className="flex items-center gap-2"><Spline className="h-4 w-4" /> Angles 90° / 45°</span>
                  <span className="font-mono text-xs">{angleSnap ? "ON" : "OFF"}</span>
                </button>
                <button data-testid="toggle-dims" onClick={() => setShowDims((v) => !v)} className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-sm ${showDims ? "bg-primary/15 border border-primary/40 text-foreground" : "bg-background/40 text-muted-foreground"}`}>
                  <span className="flex items-center gap-2"><Ruler className="h-4 w-4" /> Afficher les cotes</span>
                  <span className="font-mono text-xs">{showDims ? "ON" : "OFF"}</span>
                </button>
                <button data-testid="recenter-button" onClick={() => setRecenter((v) => v + 1)} className="flex w-full items-center justify-center gap-2 rounded-md border border-border px-3 py-2 text-sm text-muted-foreground hover:border-primary/40 hover:text-foreground">
                  <Crosshair className="h-4 w-4" /> Recentrer le plan
                </button>
              </div>
            </div>
            <div className="rounded-xl border border-border/60 bg-card p-4">
              <div className="uppercase-label mb-2 text-muted-foreground">Outils</div>
                {([
                  ["move-vertex", "Déplacer sommet", Grip],
                  ["move-wall", "Déplacer mur", Move],
                  ["draw-wall", "Dessiner un mur", PenLine],
                  ["add-door", "Ajouter une porte", DoorOpen],
                  ["add-window", "Ajouter une fenêtre", AppWindow],
                  ["correct-opening", "Corriger ouverture", DoorClosed],
                  ["draw-lowceiling", "Sous-pente < 1,80 m", Triangle],
                  ["delete", "Supprimer", Trash2],
                ] as const).map(([t, label, Icon]) => (
                  <button key={t} data-testid={`tool-${t}`} onClick={() => selectTool(t)} className={`mb-1.5 flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm ${tool === t ? (t === "delete" ? "bg-rose-500 text-white" : "bg-primary text-primary-foreground") : "bg-background/40 text-muted-foreground hover:text-foreground"}`}>
                    <Icon className="h-4 w-4" /> {label}
                  </button>
                ))}
                {tool === "draw-wall" && (
                  <div className="mt-2 rounded-md border border-cyan-500/30 bg-cyan-500/5 p-2 text-xs text-cyan-300">
                    Cliquez sur le plan pour poser des sommets reliés par des murs. Revenez sur le <span className="text-emerald-300">point de départ</span> pour fermer automatiquement la pièce.
                    <button data-testid="finish-draw-button" onClick={() => { setDrawFrom(null); setDrawStart(null); }} className="mt-2 flex w-full items-center justify-center gap-1 rounded bg-cyan-500/20 py-1.5 text-cyan-200 hover:bg-cyan-500/30"><Plus className="h-3.5 w-3.5 rotate-45" /> Terminer / nouveau tracé</button>
                  </div>
                )}
                {tool === "draw-lowceiling" && (
                  <div className="mt-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-2 text-xs text-amber-200">
                    Tracez le contour de la zone sous 1,80 m (combles/sous-pente). Revenez sur le <span className="text-emerald-300">premier point</span> pour fermer. La surface est calculée automatiquement et déduite de la Loi Carrez.
                    <button data-testid="finish-lowceiling-button" onClick={handleLowCeilClose} className="mt-2 flex w-full items-center justify-center gap-1 rounded bg-amber-500/20 py-1.5 text-amber-100 hover:bg-amber-500/30"><Triangle className="h-3.5 w-3.5" /> Terminer la zone ({lowCeilDraft.length} pts)</button>
                  </div>
                )}
                <p className="mt-2 text-xs text-muted-foreground"><span className="text-foreground">Double-cliquez</span> un mur pour éditer sa nature et son épaisseur.</p>
              </div>
              <div className="rounded-xl border border-border/60 bg-card p-4" data-testid="tool-rename-room">
                <div className="uppercase-label mb-2 flex items-center gap-1.5 text-muted-foreground"><PencilLine className="h-3.5 w-3.5" /> Renommer la pièce</div>
                <div className="flex gap-2">
                  <input value={renameVal} onChange={(e) => setRenameVal(e.target.value)} className="w-full rounded-md border border-input bg-background/60 px-2 py-1.5 text-sm outline-none focus:border-primary" />
                  <button onClick={() => renameMut.mutate()} className="rounded-md bg-primary px-3 text-sm font-semibold text-primary-foreground">OK</button>
                </div>
              </div>
              {tool === "correct-opening" && (
                <div className="rounded-xl border border-border/60 bg-card p-4">
                  <div className="uppercase-label mb-2 text-muted-foreground">Ouverture {selectedOpening ? "sélectionnée" : "— cliquez-en une"}</div>
                  {selectedOpening && (
                    <div className="space-y-3">
                      <div>
                        <div className="flex justify-between text-xs"><span>Position</span><span className="font-mono">{Math.round(selectedOpening.position * 100)}%</span></div>
                        <input type="range" min={0} max={1} step={0.01} value={selectedOpening.position} onPointerDown={() => hist.beginTransient()} onChange={(e) => updateOpening({ position: Number(e.target.value) })} className="w-full accent-cyan-400" data-testid="opening-position-slider" />
                      </div>
                      <div>
                        <div className="flex justify-between text-xs"><span>Largeur</span><span className="font-mono">{selectedOpening.width.toFixed(2)}m</span></div>
                        <input type="range" min={0.4} max={2.5} step={0.05} value={selectedOpening.width} onPointerDown={() => hist.beginTransient()} onChange={(e) => updateOpening({ width: Number(e.target.value) })} className="w-full accent-cyan-400" data-testid="opening-width-slider" />
                      </div>
                    </div>
                  )}
                </div>
              )}
              {selectedWall && (
                <div className="rounded-xl border border-border/60 bg-card p-4" data-testid="wall-props-panel">
                  <div className="uppercase-label mb-2 flex items-center justify-between text-muted-foreground">
                    <span>Mur sélectionné</span>
                    <button data-testid="wall-props-close" onClick={() => setSelWall(null)} className="text-muted-foreground hover:text-foreground"><Plus className="h-4 w-4 rotate-45" /></button>
                  </div>
                  <div className="mb-3">
                    <div className="mb-1.5 text-xs text-muted-foreground">Nature</div>
                    <div className="grid grid-cols-1 gap-1.5">
                      {(Object.keys(WALL_KINDS) as WallKind[]).map((k) => {
                        const active = wallKindOf(selectedWall.kind) === k;
                        return (
                          <button
                            key={k}
                            data-testid={`wall-kind-${k}`}
                            onClick={() => setWallKind(k)}
                            className={`flex items-center justify-between rounded-md px-3 py-2 text-sm ${active ? "bg-primary/15 border border-primary/40 text-foreground" : "bg-background/40 text-muted-foreground hover:text-foreground"}`}
                          >
                            <span className="flex items-center gap-2"><span className="inline-block h-3 w-3 rounded-sm" style={{ backgroundColor: WALL_KINDS[k].fill, opacity: 0.85 }} /> {WALL_KINDS[k].label}</span>
                            <span className="font-mono text-xs">{Math.round(WALL_KINDS[k].def * 100)} cm</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-xs"><span>Épaisseur</span><span className="font-mono" data-testid="wall-thickness-value">{Math.round(wallThicknessOf(selectedWall) * 100)} cm</span></div>
                    <input
                      type="range"
                      min={WALL_THICKNESS_MIN} max={WALL_THICKNESS_MAX} step={0.01}
                      value={wallThicknessOf(selectedWall)}
                      onPointerDown={() => hist.beginTransient()}
                      onChange={(e) => setWallThickness(Number(e.target.value))}
                      className="w-full accent-cyan-400"
                      data-testid="wall-thickness-slider"
                    />
                  </div>
                </div>
              )}
              {zoneEdit && (
                <div className="rounded-xl border border-border/60 bg-card p-4" data-testid="zone-rename-panel">
                  <div className="uppercase-label mb-2 flex items-center justify-between text-muted-foreground">
                    <span>Nom de la zone</span>
                    <button data-testid="zone-rename-close" onClick={() => setZoneEdit(null)} className="text-muted-foreground hover:text-foreground"><Plus className="h-4 w-4 rotate-45" /></button>
                  </div>
                  <input
                    autoFocus
                    data-testid="zone-name-input"
                    value={zoneEdit.name}
                    onChange={(e) => setZoneEdit({ ...zoneEdit, name: e.target.value })}
                    onKeyDown={(e) => { if (e.key === "Enter") saveZoneName(); }}
                    placeholder="ex. Salon, Cuisine…"
                    className="mb-2 w-full rounded-md border border-border bg-background/60 px-3 py-2 text-sm outline-none focus:border-primary/50"
                  />
                  <button data-testid="zone-name-save" onClick={saveZoneName} className="flex w-full items-center justify-center gap-2 rounded-md bg-primary py-2 text-sm font-semibold text-primary-foreground hover:opacity-90">
                    Enregistrer le nom
                  </button>
                </div>
              )}
              <button data-testid="save-geometry-button" onClick={() => saveMut.mutate()} disabled={saveMut.isPending} className="flex w-full items-center justify-center gap-2 rounded-md bg-emerald-500 py-2.5 text-sm font-semibold text-background hover:opacity-90 disabled:opacity-50">
                {saveMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Enregistrer (nouvelle version)
              </button>
              <p className="text-xs text-muted-foreground">Chaque enregistrement crée une nouvelle version et repasse la géométrie en « reconstruite » (re-validation requise).</p>
            </div>
            <div className="lg:col-span-3">
              <div className="mb-2 flex items-center gap-2"><GeometryBadge status={editGeom.status} /><span className="uppercase-label text-muted-foreground">Hauteur {editGeom.height}m</span></div>
              <GeometryCanvas2D geom={editGeom} editable tool={tool} selectedOpening={selOpening} selectedWall={selWall} drawFromId={drawFrom} drawStartId={drawStart} snap={snapOn ? GRID : null} angleSnap={angleSnap} showDims={showDims} showArea fitKey={`${geom?._id ?? ""}:${recenter}`} onSelectOpening={setSelOpening} onChange={hist.live} onBeginChange={hist.beginTransient} onEmptyClick={handleEmptyClick} onClosePath={handleClosePath} onWallClick={handleWallClick} onVertexClick={handleVertexClick} onOpeningClick={handleOpeningClick} onWallDoubleClick={handleWallDblClick} onZoneDoubleClick={handleZoneDblClick} lowCeilDraft={lowCeilDraft} onLowCeilPoint={handleLowCeilPoint} onLowCeilClose={handleLowCeilClose} />
              {metre && (
                <div className="mt-4 rounded-xl border border-border/60 bg-card p-4" data-testid="metre-table">
                  <div className="mb-3 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm font-semibold"><Table2 className="h-4 w-4 text-primary" /> Métré — récapitulatif</div>
                    <div className="flex gap-2">
                      <button data-testid="metre-export-csv" onClick={exportMetreCsv} className="flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs hover:border-primary/40"><FileSpreadsheet className="h-3.5 w-3.5" /> CSV</button>
                      <button data-testid="metre-export-pdf" onClick={exportMetrePdf} disabled={pdfLoading} className="flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs hover:border-primary/40 disabled:opacity-50">{pdfLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />} Métré PDF</button>
                      <button data-testid="metre-export-plan" onClick={exportPlanPdf} disabled={planLoading} className="flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs hover:border-primary/40 disabled:opacity-50">{planLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Map className="h-3.5 w-3.5" />} Plan PDF</button>
                    </div>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <div className="uppercase-label mb-1.5 text-muted-foreground">Surfaces par zone</div>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-xs text-muted-foreground">
                            <th className="pb-1 text-left font-medium">Zone</th>
                            <th className="pb-1 text-right font-medium">Au sol</th>
                            <th className="pb-1 text-right font-medium">Habitable</th>
                          </tr>
                        </thead>
                        <tbody>
                          {metre.zones.map((z, i) => (
                            <tr key={i} className="border-b border-border/40" data-testid={`metre-zone-row-${i}`}>
                              <td className="py-1.5 text-foreground">{z.name || `Zone ${i + 1}`}</td>
                              <td className="py-1.5 text-right font-mono text-emerald-300">{z.area.toFixed(2)} m²</td>
                              <td className="py-1.5 text-right font-mono text-cyan-300">{z.habitable.toFixed(2)} m²</td>
                            </tr>
                          ))}
                          <tr className="font-semibold">
                            <td className="py-1.5">Total</td>
                            <td className="py-1.5 text-right font-mono text-emerald-200" data-testid="metre-total">{metre.total.toFixed(2)} m²</td>
                            <td className="py-1.5 text-right font-mono text-cyan-200" data-testid="metre-total-habitable">{metre.totalHabitable.toFixed(2)} m²</td>
                          </tr>
                          <tr className="font-semibold text-sky-300">
                            <td className="py-1.5">Loi Carrez (≥ 1,80 m)</td>
                            <td className="py-1.5" />
                            <td className="py-1.5 text-right font-mono" data-testid="metre-carrez">{metre.carrez.toFixed(2)} m²</td>
                          </tr>
                        </tbody>
                      </table>
                      <div className="mt-2 border-t border-border/40 pt-2">
                        <div className="flex items-center justify-between gap-2 text-xs">
                          <label className="text-muted-foreground">Surface sous 1,80 m (combles/sous-pente)</label>
                          <div className="flex items-center gap-1">
                            <input
                              type="number" min={0} step={0.1}
                              data-testid="low-ceiling-input"
                              value={(editGeom.lowCeilingPolys?.length ?? 0) > 0 ? metre.lowCeiling.toFixed(2) : (editGeom.lowCeilingArea ?? 0)}
                              disabled={(editGeom.lowCeilingPolys?.length ?? 0) > 0}
                              onFocus={() => hist.beginTransient()}
                              onChange={(e) => setLowCeilingLive(Number(e.target.value))}
                              className="w-20 rounded-md border border-border bg-background/60 px-2 py-1 text-right font-mono outline-none focus:border-primary/50 disabled:opacity-60"
                            />
                            <span className="text-muted-foreground">m²</span>
                          </div>
                        </div>
                        {(editGeom.lowCeilingPolys?.length ?? 0) > 0 ? (
                          <div className="mt-2 space-y-1" data-testid="low-ceiling-poly-list">
                            {editGeom.lowCeilingPolys!.map((p, i) => (
                              <div key={i} className="flex items-center justify-between rounded-md bg-background/40 px-2 py-1 text-xs">
                                <span className="text-amber-300">Sous-pente {i + 1} — {polyArea(p).toFixed(2)} m²</span>
                                <button data-testid={`low-ceiling-poly-remove-${i}`} onClick={() => removeLowCeilPoly(i)} className="text-muted-foreground hover:text-rose-400"><Trash2 className="h-3.5 w-3.5" /></button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="mt-1 text-[11px] leading-snug text-muted-foreground">Astuce : utilisez l'outil <span className="text-amber-300">Sous-pente &lt; 1,80 m</span> pour dessiner les zones directement sur le plan — la surface est calculée automatiquement.</p>
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="uppercase-label mb-1.5 text-muted-foreground">Murs & périmètre</div>
                      <table className="w-full text-sm">
                        <tbody>
                          <tr className="border-b border-border/40"><td className="py-1.5">Périmètre (contour)</td><td className="py-1.5 text-right font-mono" data-testid="metre-perimeter">{metre.perimeter.toFixed(2)} m</td></tr>
                          {(Object.keys(WALL_KINDS) as WallKind[]).map((k) => (
                            <tr key={k} className="border-b border-border/40">
                              <td className="py-1.5"><span className="mr-2 inline-block h-2.5 w-2.5 rounded-sm align-middle" style={{ backgroundColor: WALL_KINDS[k].fill, opacity: 0.85 }} />{WALL_KINDS[k].label}</td>
                              <td className="py-1.5 text-right font-mono" data-testid={`metre-wall-${k}`}>{metre.byKind[k].toFixed(2)} m</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">Astuce : double-cliquez une étiquette de zone sur le plan pour la renommer (Salon, Cuisine…). Le PDF reflète la dernière version enregistrée.</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-border py-16 text-center text-muted-foreground">Pas encore de géométrie. Finalisez un scan puis reconstruisez.</div>
        )
      )}

      {tab === "3d" && (
        editGeom ? (
          <div className="grid gap-4 lg:grid-cols-4">
            <div className="space-y-3 lg:col-span-1">
              <div className="rounded-xl border border-border/60 bg-card p-4">
                <div className="uppercase-label mb-2 text-muted-foreground">Rotation caméra</div>
                <input type="range" min={0} max={360} value={rotation} onChange={(e) => setRotation(Number(e.target.value))} className="w-full accent-cyan-400" data-testid="rotation-slider" />
                <button data-testid="reset-camera-button" onClick={() => setRotation(35)} className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"><RotateCcw className="h-3.5 w-3.5" /> Réinitialiser</button>
              </div>
              <div className="rounded-xl border border-border/60 bg-card p-4">
                <div className="uppercase-label mb-2 text-muted-foreground">Aperçu par couche</div>
                {(["estimated", "reconstructed", "validated"] as const).map((s) => (
                  <button key={s} data-testid={`toggle-${s}-layer`} onClick={() => setStyleStatus(styleStatus === s ? undefined : s)} className={`mb-1.5 flex w-full items-center justify-between rounded-md px-3 py-2 text-sm ${styleStatus === s ? "bg-primary/20 border border-primary/40" : "bg-background/40"}`}>
                    <GeometryBadge status={s} />
                  </button>
                ))}
                <button onClick={() => setStyleStatus(undefined)} className="mt-1 text-xs text-muted-foreground hover:text-foreground">Couleur réelle ({editGeom.status})</button>
              </div>
              <div className="rounded-xl border border-border/60 bg-card p-4 space-y-2">
                {([["Murs", showWalls, setShowWalls], ["Plafond", showCeiling, setShowCeiling], ["Fil de fer", wireframe, setWireframe]] as const).map(([label, val, set]) => (
                  <label key={label} className="flex items-center justify-between text-sm">
                    <span>{label}</span>
                    <input type="checkbox" checked={val} onChange={(e) => (set as any)(e.target.checked)} className="accent-cyan-400 h-4 w-4" />
                  </label>
                ))}
              </div>
            </div>
            <div className="lg:col-span-3">
              <div className="mb-2 flex items-center gap-2"><GeometryBadge status={styleStatus || editGeom.status} /><span className="uppercase-label text-muted-foreground">Aperçu volumétrique</span></div>
              <GeometryViewer3D geom={editGeom} rotation={rotation} styleStatus={styleStatus} showWalls={showWalls} showCeiling={showCeiling} wireframe={wireframe} />
            </div>
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-border py-16 text-center text-muted-foreground">Pas de géométrie à visualiser.</div>
        )
      )}

      <ProcessingOverlay
        open={reconMut.isPending}
        title="Reconstruction de la géométrie"
        stages={["Alignement des keyframes RGB…", "Fusion IMU + ARCore…", "Optimisation (bundle adjustment)…", "Estimation de la dérive…", "Finalisation du maillage…"]}
        testId="reconstruct-progress-overlay"
      />
    </div>
  );
}
